import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  alpha,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IoStar } from 'react-icons/io5';
import { TbMessageCircle } from 'react-icons/tb';
import { BsChatLeftText } from 'react-icons/bs';
import RefreshIcon from '@mui/icons-material/Refresh';

// Color palette
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  error: '#f44336',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
};

const ResponseCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${COLORS.lightBorder}`,
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: alpha(COLORS.primary, 0.02),
  },
}));

const RatingDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const SurveyResponses = ({
  open,
  onClose,
  survey,
  responses,
  loading,
  errorMessage,
  getSurveyResponses,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeIcon = (type) => {
    return type === 'rating' ? (
      <IoStar size={20} />
    ) : (
      <TbMessageCircle size={20} />
    );
  };

  const getTypeText = (type) => {
    return type === 'rating' ? 'Calificación' : 'Texto';
  };

  const renderRating = (rating, maxRating) => {
    return (
      <RatingDisplay>
        {[...Array(maxRating)].map((_, index) => (
          <IoStar
            key={index}
            size={20}
            style={{
              color: index < rating ? COLORS.warning : '#e0e0e0',
              cursor: 'default',
            }}
          />
        ))}
        <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
          {rating}/{maxRating}
        </Typography>
      </RatingDisplay>
    );
  };

  const renderResponse = (response) => {
    return (
      <ResponseCard key={response.id}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Usuario: {response.user_id}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(response.created_at)}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        {survey.type === 'rating' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Calificación:
            </Typography>
            {renderRating(response.rating, survey.max_rating)}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Respuesta:
            </Typography>
            <Typography variant="body1" sx={{ 
              backgroundColor: alpha(COLORS.primary, 0.05), 
              p: 1, 
              borderRadius: 1,
              fontStyle: 'italic'
            }}>
              "{response.response}"
            </Typography>
          </Box>
        )}
      </ResponseCard>
    );
  };

  const handleReload = () => {
    if (survey && getSurveyResponses) {
      getSurveyResponses(survey.id);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getTypeIcon(survey?.type)}
            <Box>
              <Typography variant="h6">
                Respuestas de: {survey?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTypeText(survey?.type)} • {responses?.length || 0} respuestas
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleReload} disabled={loading || !survey} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : responses && responses.length > 0 ? (
          <Box>
            {responses.map(renderResponse)}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4,
            color: 'text.secondary'
          }}>
            <BsChatLeftText size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No hay respuestas aún
            </Typography>
            <Typography variant="body2" textAlign="center">
              Cuando los usuarios respondan a esta encuesta, sus respuestas aparecerán aquí.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyResponses; 